import type { Inquiry } from "../types";
import { EVENT_TYPE_LABELS, eventSchedule, formatTime12h } from "../types";

type Props = {
  inquiry: Inquiry;
  hasUnread?: boolean;
  onClick: () => void;
  onDelete?: () => void;
};

export function LeadCard({ inquiry, hasUnread, onClick, onDelete }: Props) {
  const daysSinceCreated = Math.floor(
    (Date.now() - new Date(inquiry.created_at).getTime()) / 86_400_000
  );
  const schedule = eventSchedule(inquiry);

  return (
    <div
      className={`relative w-full rounded-lg border p-3 text-left transition-colors hover:border-tribal-red/50 ${
        hasUnread ? "border-tribal-red/40 bg-tribal-red/5" : "border-tribal-border bg-tribal-surface"
      }`}
    >
      <button type="button" onClick={onClick} className="w-full text-left">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {hasUnread && (
            <span className="h-2 w-2 flex-shrink-0 rounded-full bg-tribal-red animate-pulse" />
          )}
          <span className="text-sm font-medium text-white">
            {inquiry.first_name} {inquiry.last_name}
          </span>
        </div>
        <span className="text-xs text-tribal-muted">{daysSinceCreated}d</span>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span className="text-xs text-tribal-muted">{schedule.groupSize} guests</span>
        <span className="text-xs text-tribal-muted">•</span>
        <span className="text-xs text-tribal-muted">{schedule.hours}hr</span>
      </div>
      <div className="mt-1">
        <span className="inline-block rounded-full bg-tribal-border px-2 py-0.5 text-xs text-tribal-muted">
          {EVENT_TYPE_LABELS[inquiry.event_type] ?? inquiry.event_type}
        </span>
      </div>
      {schedule.date && (() => {
        const eventDate = new Date(schedule.date + "T00:00:00");
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const daysUntil = Math.ceil((eventDate.getTime() - today.getTime()) / 86_400_000);
        return (
          <div className="mt-1 text-xs text-tribal-muted">
            {eventDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            {schedule.time && ` • ${formatTime12h(schedule.time)}`}
            {daysUntil > 0 && (
              <span className={daysUntil <= 7 ? "text-amber-400 ml-1" : "ml-1"}>
                ({daysUntil}d to event)
              </span>
            )}
            {daysUntil === 0 && <span className="text-green-400 ml-1">(Today)</span>}
            {daysUntil < 0 && <span className="text-tribal-muted/50 ml-1">({Math.abs(daysUntil)}d ago)</span>}
          </div>
        );
      })()}
      {inquiry.wants_callback && (
        <div className="mt-1">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-400">
            📞 Callback requested
          </span>
        </div>
      )}
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-2 right-2 rounded p-1 text-tribal-muted/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          title="Delete inquiry"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
            <path fillRule="evenodd" d="M5 3.25V4H2.75a.75.75 0 0 0 0 1.5h.3l.815 8.15A1.5 1.5 0 0 0 5.357 15h5.285a1.5 1.5 0 0 0 1.493-1.35l.815-8.15h.3a.75.75 0 0 0 0-1.5H11v-.75A2.25 2.25 0 0 0 8.75 1h-1.5A2.25 2.25 0 0 0 5 3.25Zm2.25-.75a.75.75 0 0 0-.75.75V4h3v-.75a.75.75 0 0 0-.75-.75h-1.5ZM6.05 6a.75.75 0 0 1 .787.713l.275 5.5a.75.75 0 0 1-1.498.075l-.275-5.5A.75.75 0 0 1 6.05 6Zm3.9 0a.75.75 0 0 1 .712.787l-.275 5.5a.75.75 0 0 1-1.498-.075l.275-5.5a.75.75 0 0 1 .786-.711Z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
}
